import { useEffect, useMemo } from "react";
import { useTheme } from "@mui/material";
import cn from "../../utils/cn";
import { BulletList, Chips, SECTION_LABELS, ScoreBar, formatDuration, formatTime, humanize, isScore, toText } from "./aiShared";

const OUTCOME_LABELS = {
  answered: "Answered",
  partial: "Partial",
  dont_know: "Didn't know",
  skipped: "Skipped",
  no_response: "No response",
};

const QuestionCard = ({ question, highlighted, children }) => {
  const theme = useTheme();
  const evaluation = question.evaluation;
  const flags = Object.entries(question.flags || {}).filter(([, value]) => value).map(([key]) => humanize(key));
  const muted = { color: theme.palette.text.secondary || "#6b7280" };

  return (
    <div
      id={`ai-question-${question.question_id}`}
      className={cn("rounded-lg border p-3 flex flex-col gap-2 scroll-mt-4", highlighted && "ring-2 ring-blue-500")}
      style={{ borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm font-medium">
          <span style={muted}>Q{question.seq}. </span>{toText(question.question)}
        </p>
        <span className="text-sm font-semibold whitespace-nowrap">
          {isScore(evaluation?.score)
            ? `${evaluation.score.toFixed(1)}/10`
            : question.eval_status === "pending" || question.eval_status === "running"
              ? "Scoring..."
              : ""}
        </span>
      </div>
      <p className="text-xs" style={muted}>
        {[
          SECTION_LABELS[question.section_id] || humanize(question.section_id),
          question.source && question.source !== "planned" ? humanize(question.source) : "",
          toText(question.skill),
          humanize(toText(question.difficulty)),
          OUTCOME_LABELS[question.outcome] || humanize(question.outcome),
          question.response_duration_seconds ? formatDuration(question.response_duration_seconds) : "",
        ].filter(Boolean).join(" · ")}
      </p>
      <p className="text-sm whitespace-pre-line rounded bg-gray-50 px-3 py-2">
        {toText(question.candidate_answer) || <span style={muted}>No answer recorded.</span>}
      </p>
      {flags.length > 0 && <Chips items={flags} className="bg-amber-50 border-amber-300 text-amber-900" />}
      {evaluation && (
        <details className="text-sm">
          <summary className="cursor-pointer text-xs font-medium text-blue-700">Evaluation</summary>
          <div className="flex flex-col gap-3 mt-2">
            {evaluation.dimensions && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {Object.entries(evaluation.dimensions).map(([key, value]) => (
                  <ScoreBar key={key} label={humanize(key)} value={value} />
                ))}
              </div>
            )}
            {[
              ["Covered", evaluation.covered_points],
              ["Missing", evaluation.missing_points],
              ["Incorrect", evaluation.incorrect_statements],
              ["Strengths", evaluation.strengths],
            ].map(([label, items]) => items?.length > 0 && (
              <div key={label}>
                <p className="text-xs font-medium">{label}</p>
                <BulletList items={items} />
              </div>
            ))}
            {evaluation.evaluator_notes && <p className="text-xs" style={muted}>{toText(evaluation.evaluator_notes)}</p>}
          </div>
        </details>
      )}
      {children}
    </div>
  );
};

// Asked questions with answers and evaluations; follow-ups threaded under their parent via parent_question_id
export const QuestionsView = ({ questions, highlightId }) => {
  const threads = useMemo(() => {
    const byId = Object.fromEntries(questions.map((question) => [question.question_id, question]));
    const roots = questions.filter((question) => !question.parent_question_id || !byId[question.parent_question_id]);
    const childrenOf = (question) =>
      questions.filter((item) => item.parent_question_id === question.question_id).sort((a, b) => a.seq - b.seq);
    return { roots: roots.sort((a, b) => a.seq - b.seq), childrenOf };
  }, [questions]);

  useEffect(() => {
    if (!highlightId) return;
    document.getElementById(`ai-question-${highlightId}`)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [highlightId]);

  if (!questions.length) return <p className="text-sm">No questions yet.</p>;

  const renderThread = (question) => {
    const followUps = threads.childrenOf(question);
    return (
      <QuestionCard key={question.question_id} question={question} highlighted={question.question_id === highlightId}>
        {followUps.length > 0 && (
          <div className="flex flex-col gap-2 pl-4 border-l-2 border-gray-200">{followUps.map(renderThread)}</div>
        )}
      </QuestionCard>
    );
  };

  return <div className="flex flex-col gap-3">{threads.roots.map(renderThread)}</div>;
};

export const speakerLabel = (segment) => {
  switch (segment.speaker) {
    case "interviewer":
      return "AI interviewer";
    case "candidate":
      return "Candidate";
    case "panelist":
      return segment.speaker_name || "Panelist";
    case "other":
      return segment.speaker_name ? `${segment.speaker_name} (not on the interview)` : "Other participant";
    default:
      return segment.speaker_name || "Unknown speaker";
  }
};

const SPEAKER_BUBBLES = {
  interviewer: "bg-gray-100",
  candidate: "bg-blue-50",
  panelist: "bg-violet-50",
};

export const TranscriptView = ({ segments }) => {
  const theme = useTheme();
  if (!segments.length) return <p className="text-sm">No transcript yet.</p>;
  return (
    <div className="flex flex-col gap-2">
      {segments.map((segment) => {
        const isCandidate = segment.speaker === "candidate";
        return (
          <div key={segment.seq} className={cn("flex flex-col max-w-[85%]", isCandidate ? "self-end items-end" : "self-start")}>
            <span className="text-xs" style={{ color: theme.palette.text.secondary || "#6b7280" }}>
              {speakerLabel(segment)}
              {segment.created_at ? ` · ${formatTime(segment.created_at)}` : ""}
            </span>
            <p className={cn("text-sm rounded-lg px-3 py-2 whitespace-pre-line", SPEAKER_BUBBLES[segment.speaker] || "bg-amber-50")}>
              {toText(segment.text)}
            </p>
          </div>
        );
      })}
    </div>
  );
};
